var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/naming-convention, react/require-default-props */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../../../atoms/Button';
import { TextInput } from '../../../molecules/inputs/form/TextInput';
import './GeneralActionsStyles.scss';
const BASE_CLASS = 'qmb-ui-general-actions';
const COPY_FEEDBACK_MS = 2000;
export function GeneralActions({ responseId, feedbackId, turnNumber, agentResponseText = '', onFeedback, onRetry, onCopy }) {
    const [feedback, setFeedback] = useState('none');
    const [copied, setCopied] = useState(false);
    const [pendingDownvote, setPendingDownvote] = useState(false);
    const [comment, setComment] = useState('');
    const commentInputRef = useRef(null);
    const responseIdForApi = (responseId || feedbackId).trim();
    const feedbackReady = Boolean(responseIdForApi) && Number.isFinite(turnNumber) && turnNumber >= 1;
    useEffect(() => {
        if (pendingDownvote && commentInputRef.current) {
            commentInputRef.current.focus();
        }
    }, [pendingDownvote]);
    const handleCopy = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a;
        const text = agentResponseText.trim();
        if (!text) {
            return;
        }
        try {
            if (onCopy) {
                onCopy(text);
            }
            else if ((_a = navigator.clipboard) === null || _a === void 0 ? void 0 : _a.writeText) {
                yield navigator.clipboard.writeText(text);
            }
            else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
        }
        catch (_b) {
            console.warn('Failed to copy to clipboard');
        }
    }), [agentResponseText, onCopy]);
    const handleThumbsUp = useCallback(() => {
        if (!feedbackReady)
            return;
        const newRating = feedback === 'thumbs_up' ? 'none' : 'thumbs_up';
        setFeedback(newRating);
        setPendingDownvote(false);
        if (newRating !== 'none') {
            onFeedback === null || onFeedback === void 0 ? void 0 : onFeedback(responseIdForApi, turnNumber, 'thumbs_up');
        }
    }, [feedback, feedbackReady, onFeedback, responseIdForApi, turnNumber]);
    const handleThumbsDown = useCallback(() => {
        if (!feedbackReady)
            return;
        if (feedback === 'thumbs_down') {
            setFeedback('none');
            setPendingDownvote(false);
            return;
        }
        setFeedback('none');
        setPendingDownvote(true);
    }, [feedback, feedbackReady]);
    const submitDownvote = useCallback((feedbackComment) => {
        const trimmed = (feedbackComment !== null && feedbackComment !== void 0 ? feedbackComment : comment).trim();
        setFeedback('thumbs_down');
        setPendingDownvote(false);
        setComment('');
        onFeedback === null || onFeedback === void 0 ? void 0 : onFeedback(responseIdForApi, turnNumber, 'thumbs_down', trimmed || undefined);
    }, [comment, onFeedback, responseIdForApi, turnNumber]);
    const handleDownvoteSubmit = useCallback(() => {
        submitDownvote();
    }, [submitDownvote]);
    const handleDownvoteSkip = useCallback(() => {
        submitDownvote('');
    }, [submitDownvote]);
    const handleCommentKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            submitDownvote();
        }
    }, [submitDownvote]);
    const handleRetry = useCallback(() => {
        onRetry === null || onRetry === void 0 ? void 0 : onRetry();
    }, [onRetry]);
    return (React.createElement("div", { className: BASE_CLASS, "data-response-id": responseId },
        React.createElement("div", { className: `${BASE_CLASS}__row` },
            React.createElement(Button, { className: `${BASE_CLASS}__btn`, onClick: () => {
                    void handleCopy();
                }, "aria-label": "Copy response", disabled: !agentResponseText.trim() },
                React.createElement("i", { className: copied ? 'fa-solid fa-check' : 'fa-light fa-copy', style: copied ? { color: 'var(--indigo-500)' } : undefined })),
            React.createElement(Button, { className: `${BASE_CLASS}__btn ${feedback === 'thumbs_up' ? `${BASE_CLASS}__btn--active` : ''}`, onClick: handleThumbsUp, "aria-label": "Rate up", "aria-pressed": feedback === 'thumbs_up', disabled: !feedbackReady },
                React.createElement("i", { className: feedback === 'thumbs_up' ? 'fa-solid fa-thumbs-up' : 'fa-light fa-thumbs-up' })),
            React.createElement(Button, { className: `${BASE_CLASS}__btn ${feedback === 'thumbs_down' || pendingDownvote ? `${BASE_CLASS}__btn--active` : ''}`, onClick: handleThumbsDown, "aria-label": "Rate down", "aria-pressed": feedback === 'thumbs_down' || pendingDownvote, disabled: !feedbackReady },
                React.createElement("i", { className: feedback === 'thumbs_down' || pendingDownvote ? 'fa-solid fa-thumbs-down' : 'fa-light fa-thumbs-down' })),
            React.createElement(Button, { className: `${BASE_CLASS}__btn`, onClick: handleRetry, "aria-label": "Try again" },
                React.createElement("i", { className: "fa-light fa-rotate-right" }))),
        pendingDownvote && (React.createElement("div", { className: `${BASE_CLASS}__downvote-prompt` },
            React.createElement("div", { className: `${BASE_CLASS}__downvote-input` },
                React.createElement(TextInput, { ref: commentInputRef, id: `feedback-comment-${responseId}`, value: comment, onChange: (e) => setComment(e.target.value), onKeyDown: handleCommentKeyDown, placeholder: "What didn't you like about this response?", "aria-label": "Feedback comment" })),
            React.createElement(Button, { variant: "primary", onClick: handleDownvoteSubmit }, "Submit"),
            React.createElement(Button, { onClick: handleDownvoteSkip }, "Skip")))));
}
